import browserDetect from 'browser-detect';
const browser = browserDetect();

import { DateTime, Info } from 'luxon';
import Cleave from 'cleave.js';
import 'cleave.js/dist/addons/cleave-phone.US';

const cssClasses = {
    attachmentsList: 'js-attachments',
    attachmentsListForNoContent: 'js-attachments-no-content',
    count: 'js-count',
    creditCardType: 'js-credit-card',
    numericType: 'js-numeric',
    cvvType: 'js-cvv',
    dateType: 'js-date',
    fileUploader: 'js-file-uploader',
    hidden: 'hidden',
    routingNumberType: 'js-routing-number',
    submittingHide: 'js-submitting-hide',
    submittingShow: 'js-submitting-show',
    usesCleave: 'js-cleave',
    validatableControl: 'js-validate',
    validating: 'validating',
    validationFieldsetSummary: 'js-validation-fieldset-summary',
    validationScope: 'js-validation-scope',
    validationSummary: 'js-validation-summary',
    zipCodeType: 'js-zip-code',
};

const formControlsSelector = 'fieldset, input, select, textarea';

const generalErrorMessageKey = 'general';

export default class ClientSideValidation {
    constructor() {
        const pageLoadOriginal = window.pageLoad;
        window.pageLoad = function() {
            if (pageLoadOriginal) { pageLoadOriginal(); }
            this._init();
        }.bind(this);
    }

    static isValidRoutingNumber(value) {
        if (value.length !== 9 || value.charAt(0) === 5) { return false; }

        const digits = value.split('');

        for (let i = 0; i < digits.length; i++) {
            let val = digits[i];

            val = Number.parseInt(val, 10);

            if (!Number.isInteger(val)) {
                return false;
            }

            digits[i] = val;
        }

        const checksum = ((3 * (digits[0] + digits[3] + digits[6]))
            + (7 * (digits[1] + digits[4] + digits[7]))
            + (digits[2] + digits[5] + digits[8])) % 10;

        if (checksum !== 0) {
            return false;
        }

        return true;
    }

    doCustomValidation(evt, elem) {
        if (elem.dataset.customValidation) {
            const validation = JSON.parse(elem.dataset.customValidation);
            return this[validation.funcName](evt, elem, ...validation.args);
        }

        return true;
    }

    onInvalid(evt) {
        const element = evt.target;

        this._renderErrorMessages(element);
    }

    validateForm(element) {
        let formIsValid = true;

        const parent = this._getFormScopeElement(element);

        parent.classList.add(cssClasses.validating);

        /* ASP.NET Web Forms doesn't necessarily raise the client-side `submit` event, 
            so we need to loop through the controls and check them. */
        const controls = parent.querySelectorAll(formControlsSelector);

        for (let i = 0; i < controls.length; i++) {
            const elem = controls[i];

            this.doCustomValidation(null, elem);

            const fieldIsValid = elem.checkValidity();

            if (formIsValid && !fieldIsValid) {
                formIsValid = false;
            }
        }

        if (formIsValid) {
            parent.classList.remove(cssClasses.validating);
            Array.from(parent.getElementsByClassName(cssClasses.validating))
                .forEach(elem => elem.classList.remove(cssClasses.validating));

            // Hide any server-side validation message (from earlier submits)
            Array.from(parent.getElementsByClassName(cssClasses.validationSummary))
                .forEach(elem => this._getClearedErrorContainer(elem));
        }

        return formIsValid;
    }

    onFormControlChange(evt) {
        const element = evt.currentTarget;
        element.classList.add(cssClasses.validating);
        element.dataset.isDirty = true;

        const parent = this._getFormScopeElement(element);

        this._getClearedErrorContainer(element);
        this._getClearedSummaryContainer(element, parent);

        if (!this.doCustomValidation(evt, element)) {
            element.checkValidity();
            return true;
        }

        if (element.type === 'file') {
            this._validateFileInput(element);
        }

        // Handle type="date" in browsers that don't support it.
        if (element.type === 'text' && element.classList.contains(cssClasses.dateType)) {
            this._validateDateForLegacyBrowsers(element);
        }

        element.checkValidity();

        return true;
    }

    toggleControlsforSubmit(ctx) {
        const parent = this._getFormScopeElement(ctx);

        Array.from(parent.getElementsByClassName(cssClasses.submittingHide))
            .forEach(elem => elem.style.display = 'none');
        Array.from(parent.getElementsByClassName(cssClasses.submittingShow))
            .forEach(elem => elem.style.display = 'block');

        return true; // Allow other click events.
    }

    _fieldHasData(elem) {
        switch (elem.type) {
            case 'text': {
                if (elem.value) {
                    return true;
                }
                break;
            }
            case 'checkbox':
            case 'radio': {
                if (elem.checked) {
                    return true;
                }
                break;
            }
        }

        return false;
    }

    _formatServerDate(element) {
        if (element.value) {
            const date = DateTime.fromFormat(element.value, 'yyyy-MM-dd');
            element.value = date.isValid ? date.toFormat('MM/dd/yyyy') : '';
        }
    }

    /** 
     * Clears the error container and returns it.
     * 
     * @param {HTMLElement} element
     * @param {string} [errorContainerSelector]
     * @returns {HTMLElement}
     */
    _getClearedErrorContainer(element, errorContainerSelector) {
        const errorContainer = errorContainerSelector ?
            element.querySelector(errorContainerSelector) :
            document.getElementById(element.dataset.errorMessageContainerId); 

        if (!errorContainer) { return; }

        // Clear errorContainer.
        while (errorContainer.firstChild) {
            errorContainer.removeChild(errorContainer.firstChild);
        }

        return errorContainer;
    }

    /** 
     * Clears the summary error container and returns it.
     * 
     * @param {HTMLElement} element
     * @param {HTMLElement} parent
     * @returns {HTMLElement}
     */
    _getClearedSummaryContainer(element, parent) {
        const errorContainer = parent.querySelector(`.${cssClasses.validationSummary}`);
        if (!errorContainer) { return; }

        // Clear error messages *for this field* from the summary errorContainer.
        Array.from(errorContainer.querySelectorAll(`[data-field-key="${element.id || element.name}"]`))
            .forEach(elem => {
                errorContainer.removeChild(elem);
            });

        return errorContainer;
    }

    _getFormScopeElement(element) {
        let parent = element;
        do {
            parent = parent.parentElement;
        } while (parent
            && parent.tagName.toLowerCase() !== 'form'
            && !parent.classList.contains(cssClasses.validationScope));

        return parent;
    }

    _init() {
        Array.from(document.getElementsByClassName(cssClasses.validatableControl)).forEach(control => {
            switch (control.type) {
                case 'text': {
                    if (control.classList.contains(cssClasses.dateType)) {
                        this._formatServerDate(control);
                    }
                    if (control.classList.contains(cssClasses.creditCardType)) {
                        this._initAsYouTypeFormattingForCreditCardNumber(control);
                    }
                    if (control.classList.contains(cssClasses.routingNumberType)) {
                        this._initAsYouTypeFormattingForNumeric(control);
                    }
                    if (control.classList.contains(cssClasses.cvvType)) {
                        this._initAsYouTypeFormattingForNumeric(control);
                    }
                    if (control.classList.contains(cssClasses.zipCodeType)) {
                        this._initAsYouTypeFormattingForZipCode(control);
                    }
                    if (control.classList.contains(cssClasses.numericType)) {
                        this._initAsYouTypeFormattingForNumeric(control);
                    }
                    break;
                }
                case 'tel': {
                    this._initAsYouTypeFormattingForTelephoneNumber(control);
                    break;
                }
            }

            control.addEventListener('invalid', this.onInvalid.bind(this));

            // AEGCLIEN-506
            if ((browser.name === 'ie' || browser.name === 'edge') && control.tagName.toLowerCase() === 'select') {
                control.addEventListener('change', (evt => {
                    const instance = this;
                    const clonedEvt = Object.assign({}, evt);
                    clonedEvt.currentTarget = evt.target;
                    window.setTimeout(() => { instance.onFormControlChange(clonedEvt); }, 0);
                }).bind(this));
            }

            // AEGCLIEN-496
            if ((browser.name === 'ie' || browser.name === 'edge' || browser.name === 'safari') &&
                control.classList.contains(cssClasses.usesCleave)) {
                control.addEventListener('input', () => {
                    control.dataset.isDirty = true;
                });
                control.addEventListener('blur', (evt => {
                    if (control.dataset.isDirty) {
                        this.onFormControlChange(evt);
                    }
                }).bind(this));
            }
        });

        Array.from(document.querySelectorAll(`.${cssClasses.fileUploader}`)).forEach(control => {
            this._initFileUploader(control);
        });
    }

    _initAsYouTypeFormattingForCreditCardNumber(control) {
        new Cleave(control, {
            creditCard: true,
            delimiter: '-',
            delimiterLazyShow: true,
            onCreditCardTypeChanged: type => {
                control.classList.add(cssClasses.validating);
                if (type === 'mastercard' || type === 'visa') {
                    control.setCustomValidity('');
                    return;
                }
                control.setCustomValidity('INVALID');
            },
        });
    }

    _initAsYouTypeFormattingForNumeric(control) {
        new Cleave(control, {
            numeral: true,
            numeralThousandsGroupStyle: 'none',
            numeralPositiveOnly: true,
            numeralDecimalScale: 0,
            stripLeadingZeroes: false,
            numericOnly: true,
        });
    }

    _initAsYouTypeFormattingForTelephoneNumber(control) {
        new Cleave(control, {
            phone: true,
            phoneRegionCode: 'US',
            delimiter: '-',
            delimiterLazyShow: true,
        });
    }

    _initAsYouTypeFormattingForZipCode(control) {
        new Cleave(control, {
            numericOnly: true,
            blocks: [5, 4],
            delimiter: '-',
            delimiterLazyShow: true,
        });
    }

    _mustMatchDate(evt, element, value) {
        const targetDate = DateTime.fromFormat(value, 'yyyy-MM-dd');
        const inputDate = element.type === 'date' ?
            DateTime.fromFormat(element.value, 'yyyy-MM-dd') :
            DateTime.fromFormat(element.value.trim(), 'M/d/yyyy');
        if (inputDate.equals(targetDate)) {
            element.setCustomValidity('');
            return true;
        }
        element.setCustomValidity('INVALID');
        return false;
    }

    _mustMatchField(evt, element, value) {
        const source = document.getElementById(value);
        if (!element || !element.value || !source || !source.value) { return; }

        if (element.value === source.value) {
            element.setCustomValidity('');
        } else {
            element.setCustomValidity('INVALID');
        }
    }

    _raiseForgotPasswordEvent(evt, element, userNameDomId) {
        const userNameElement = document.getElementById(userNameDomId);

        userNameElement.classList.add(cssClasses.validating);

        if (!userNameElement.checkValidity()) {
            return false;
        }

        return true;
    }

    _renderErrorMessages(element, errorContainerSelector, isCustomRuleValid) {
        const parent = this._getFormScopeElement(element);

        const errorContainer = this._getClearedErrorContainer(element, errorContainerSelector);
        const summaryErrorContainer = this._getClearedSummaryContainer(element, parent);

        if (!errorContainer && !summaryErrorContainer) { return; }

        // Basic field validation uses a mapping of errorMessages to `ValidityState` keys.
        let errorMessages = element.dataset.errorMessages;

        // Custom field validataion uses a single error message.
        const errorMessage = element.dataset.errorMessage;
        if (!errorMessages && !errorMessage) {
            console.error(`No error messages are defined for ${element.id || element.name}`);
            return;
        }

        const errorList = document.createElement('ul');
        // elem.dataset.fieldKey is <tagName data-field-key="" />
        errorList.dataset.fieldKey = element.id || element.name;

        if (errorMessages) {
            errorMessages = JSON.parse(errorMessages);

            Object.entries(errorMessages).forEach(elem => {
                const key = elem[0];
                const value = elem[1];

                if (!value || key === generalErrorMessageKey) { return; }

                if (element.validity[key]) {
                    errorList.appendChild(createErrorListItemElement(value));
                }
            });

            if (!errorList.firstChild && errorMessages[generalErrorMessageKey]) {
                errorList.appendChild(createErrorListItemElement(errorMessages[generalErrorMessageKey]));
            } else {
                // console.warn(`No error message is defined for ${element.id || element.name}`, element.validity);
            }
        } else if (errorMessage && !isCustomRuleValid) {
            errorList.appendChild(createErrorListItemElement(errorMessage));
        }

        if (errorList.firstChild) {
            if (errorContainer) {
                errorContainer.appendChild(errorList);
            }

            if (summaryErrorContainer) {
                summaryErrorContainer.appendChild(errorList.cloneNode(true));
            }
        }

        function createErrorListItemElement(message) {
            const errorListItem = document.createElement('li');
            errorListItem.textContent = message;
            return errorListItem;
        }
    }

    _validateExpirationDate(evt, element, monthDomId, yearDomId) {
        const now = DateTime.local();
        const expiration = {};

        const currentYearAsString = `${now.year}`;
        expiration.year = document.getElementById(yearDomId);
        expiration.month = document.getElementById(monthDomId);

        // Ensure only the current and future months are options.
        if (currentYearAsString === expiration.year.value) {
            while (expiration.month.children[1] &&
                parseInt(expiration.month.children[1].value, 10) < now.month) {
                expiration.month.removeChild(expiration.month.children[1]);
            }
        }

        // Ensure there are 12 months to choose from.
        // Length is 13 because of the initial value in the drop-down.
        if (currentYearAsString !== expiration.year.value && expiration.month.options.length !== 13) {
            let month = parseInt(expiration.month.children[1].value, 10) - 1;
            while (month > 0) {
                const optionElement = document.createElement('option');
                optionElement.value = month;
                optionElement.textContent = `${Info.months('2-digit')[month - 1]} - ${Info.months()[month - 1]}`;
                expiration.month.insertBefore(optionElement, expiration.month.children[1]);
                month = parseInt(expiration.month.children[1].value, 10) - 1;
            }
        }

        if (expiration.year.value && expiration.month.value) {
            element.classList.remove('invalid');
            this._renderErrorMessages(element, `.${cssClasses.validationFieldsetSummary}`, true);
            return true;
        } else {
            element.classList.add('invalid');
            this._renderErrorMessages(element, `.${cssClasses.validationFieldsetSummary}`, false);
            return false;
        }
    }

    _initFileUploader(element) {
        element.querySelector('label.btn').addEventListener('click', event => event.stopPropagation());

        const fileUploader = element.querySelector('input[type=file]');
        element.querySelector(`.${cssClasses.attachmentsList} .btn`)
            .addEventListener('click', () => {
                fileUploader.value = '';
                fileUploader.onchange({ currentTarget: fileUploader });
            });
    }

    _validateFileInput(element) {
        this._toggleAttachmentsList(element);

        if (element.files.length) {
            if (!this._hasValidFileTypes(element.files, element.accept.split(','))) {
                element.setCustomValidity('INVALID');
                return;
            }

            const maxSize = parseInt(element.dataset.maxBytes, 10);
            if (!this._hasValidFileSizes(element.files, maxSize)) {
                element.value = '';
                element.setCustomValidity('INVALID');
                return;
            }
        }

        element.setCustomValidity('');
    }

    _toggleAttachmentsList(element) {
        // Find parent element of control.
        let parent = element;
        do {
            parent = parent.parentElement;
        } while (parent && !parent.classList.contains(cssClasses.fileUploader));

        const attachmentsList = parent.getElementsByClassName(cssClasses.attachmentsList)[0];
        const attachmentsListForNoContent = parent.getElementsByClassName(cssClasses.attachmentsListForNoContent)[0];
        const countElement = parent.getElementsByClassName(cssClasses.count)[0];

        const firstChild = attachmentsList.querySelector('ul');
        if (firstChild) { attachmentsList.removeChild(firstChild); }

        if (!element.files || !element.files.length) {
            countElement.textContent = '';
            attachmentsList.classList.add(cssClasses.hidden);
            attachmentsListForNoContent.classList.remove(cssClasses.hidden);
        } else {
            countElement.textContent = `(${element.files.length})`;
            attachmentsListForNoContent.classList.add(cssClasses.hidden);
            const listElement = document.createElement('ul');

            for (let i = 0; i < element.files.length; i++) {
                const listItemElement = document.createElement('li');
                listItemElement.textContent = element.files[i].name;
                listElement.appendChild(listItemElement);
            }

            attachmentsList.insertBefore(listElement, attachmentsList.firstChild);

            attachmentsList.classList.remove(cssClasses.hidden);
        }
    }

    _hasValidFileTypes(files, validExtensions) {
        if (!validExtensions) { return true; }

        for (let i = 0; i < files.length; i++) {
            const file = files[i];

            // Get file extension.
            const matches = file.name.match(/\.[^\.]*$/);
            const ext = matches ? matches[0] : '';

            // Ensure that the file extension is one in the `accepts` attribute.
            for (let i = 0; i < validExtensions.length; i++) {
                if (ext.toLowerCase() === validExtensions[i]) {
                    return true;
                }
            }
        }

        return false;
    }

    _hasValidFileSizes(files, maxSize) {
        let totalSize = 0;
        for (let i = 0; i < files.length; i++) {
            totalSize += files[i].size;
            if (totalSize > maxSize) { return false; }
        }

        return true;
    }

    _validateFieldsetHasData(evt, element, fieldsetId) {
        let elem;
        if (fieldsetId && typeof(fieldsetId) === 'string') {
            elem = document.getElementById(fieldsetId);
        } else {
            elem = element;
        }

        elem.classList.add(cssClasses.validating);

        const controls = elem.querySelectorAll(formControlsSelector);

        let fieldIsValid = false;
        for (let i = 0; i < controls.length; i++) {
            const control = controls[i];

            switch (control.type) {
                case 'text': {
                    if (control.value) {
                        fieldIsValid = true;
                    }
                    break;
                }
                case 'checkbox':
                case 'radio': {
                    if (control.checked) {
                        fieldIsValid = true;
                    }
                    break;
                }
            }

            if (fieldIsValid) { break; }
        }


        if (fieldIsValid) {
            elem.classList.remove('invalid');
        } else {
            elem.classList.add('invalid');
        }

        this._renderErrorMessages(elem, `.${cssClasses.validationFieldsetSummary}`, fieldIsValid);
        return fieldIsValid;
    }

    _validateRoutingNumber(evt, element) {
        if (ClientSideValidation.isValidRoutingNumber(element.value)) {
            element.setCustomValidity('');
        } else {
            element.setCustomValidity('INVALID');
        }
    }

    _validateTaxClassificationInput(evt, element, parentIdchildIdMap) {
        if (!evt) { return; }

        element.classList.add(cssClasses.validating);

        const targetId = evt.target.dataset.parentId ? evt.target.dataset.parentId : evt.target.id;

        const map = new Map(parentIdchildIdMap);
        map.forEach((childId, parentId) => {
            const child = document.getElementById(childId);
            child.required = (targetId === parentId);
            this._getClearedErrorContainer(child);
            child.checkValidity();
        });
    }

    _validateDateForLegacyBrowsers(element) {
        const input = element.value.trim();

        // UI enforces this format for input.
        const dateTime = DateTime.fromFormat(input, 'M/d/yyyy');
        if (!dateTime.isValid) {
            element.setCustomValidity('INVALID');
            return;
        }

        // `min` and `max` are in a different format for compatibility with the server-side validation.
        const minAttrValue = element.getAttribute('min');
        const maxAttrValue = element.getAttribute('max');

        const min = minAttrValue ? DateTime.fromFormat(minAttrValue, 'yyyy-MM-dd') : null;
        const max = maxAttrValue ? DateTime.fromFormat(maxAttrValue, 'yyyy-MM-dd') : null;

        // If there is no valid `min` or `max` constraint, then any date works.
        if ((!min || !min.isValid) && (!max || !max.isValid)) {
            element.setCustomValidity('');
            return;
        }

        // If there is a `min` constraint, then ensure date is not less than it.
        if (min && min.isValid && dateTime < min) {
            element.setCustomValidity('INVALID');
            return;
        }

        // If there is a `max` constraint, then ensure date is not less than it.
        if (max && max.isValid && dateTime > max) {
            element.setCustomValidity('INVALID');
            return;
        }

        element.setCustomValidity('');
    }
}
